import React, { useState } from 'react';
import { 
  Network, 
  ShieldCheck, 
  ShieldAlert, 
  AlertTriangle, 
  Lock, 
  CheckCircle2, 
  XCircle, 
  ArrowRight, 
  Search, 
  Filter, 
  Cpu, 
  Database, 
  DollarSign, 
  Users, 
  Cloud, 
  MessageSquare, 
  Terminal, 
  ExternalLink,
  Layers,
  ChevronDown,
  ChevronUp,
  KeyRound
} from 'lucide-react';

export interface ConnectedApp {
  id: string;
  name: string;
  category: 'fintech' | 'crm' | 'devops' | 'database' | 'messaging';
  categoryLabel: string;
  icon: string;
  description: string;
  endpoint: string;
  permissionTier: 'ALLOW' | 'REVIEW' | 'BLOCK';
  connectedAgents: Array<{ id: string; name: string }>;
  allowedActions: string[];
  reviewActions: string[];
  blockedActions: string[];
  rateLimit: string;
  financialCap?: string;
  enforcedGuardrails: string[];
  lastAccessed: string;
}

const APPS_DATA: ConnectedApp[] = [
  {
    id: 'app-swift-fedwire',
    name: 'Swift / Fedwire Banking Network',
    category: 'fintech',
    categoryLabel: 'Financial & ERP',
    icon: '🏦',
    description: 'High-value interbank electronic fund transfer gateway.',
    endpoint: 'wire://swift.fedwire/transfer',
    permissionTier: 'REVIEW',
    connectedAgents: [
      { id: 'agent-invoice-01', name: 'Financial Invoice & Disbursement Agent' },
      { id: 'agent-procure-02', name: 'Procurement Settlement AI' }
    ],
    allowedActions: ['verify_account_routing', 'get_transfer_status', 'list_settled_batches'],
    reviewActions: ['disburse_wire_transfer', 'update_settlement_account', 'refund_transaction'],
    blockedActions: ['unapproved_offshore_wire', 'bypass_maker_checker', 'override_spend_limit'],
    rateLimit: '20 calls/min',
    financialCap: '$5,000.00 / txn max ($25k / day)',
    enforcedGuardrails: ['RULE_SPEND_VELOCITY_CAP', 'DUAL_CONTROL_MAKER_CHECKER', 'PROMPT_INJECTION_OVERRIDE_SHIELD'],
    lastAccessed: '2 minutes ago'
  },
  {
    id: 'app-stripe',
    name: 'Stripe Payments & Billing',
    category: 'fintech',
    categoryLabel: 'Financial & ERP',
    icon: '💳',
    description: 'Subscription billing, charge verification, and merchant payouts.',
    endpoint: 'https://api.stripe.com/v1',
    permissionTier: 'ALLOW',
    connectedAgents: [
      { id: 'agent-invoice-01', name: 'Financial Invoice & Disbursement Agent' }
    ],
    allowedActions: ['retrieve_charge', 'list_invoices', 'verify_payment_intent', 'get_customer_balance'],
    reviewActions: ['issue_refund_over_500', 'apply_account_credit'],
    blockedActions: ['delete_customer_card', 'export_raw_pan_numbers', 'modify_merchant_payout_bank'],
    rateLimit: '120 calls/min',
    financialCap: '$500.00 / refund',
    enforcedGuardrails: ['PCI_DSS_CARD_MASKING', 'SPEND_VELOCITY_CAP'],
    lastAccessed: '14 minutes ago'
  },
  {
    id: 'app-salesforce',
    name: 'Salesforce Enterprise CRM',
    category: 'crm',
    categoryLabel: 'CRM & Customer Ops',
    icon: '☁️',
    description: 'Customer records, enterprise deal pipelines, and account contacts.',
    endpoint: 'https://acme.my.salesforce.com/services/data/v59.0',
    permissionTier: 'ALLOW',
    connectedAgents: [
      { id: 'agent-support-01', name: 'Customer Support Triage Agent' },
      { id: 'agent-lead-03', name: 'Sales Pipeline Assistant' }
    ],
    allowedActions: ['query_account_by_id', 'read_deal_stage', 'update_lead_status', 'add_case_comment'],
    reviewActions: ['reassign_enterprise_account', 'bulk_update_deals_over_100k'],
    blockedActions: ['export_all_contacts_csv', 'unmasked_ssn_lookup', 'delete_account_record'],
    rateLimit: '300 calls/min',
    enforcedGuardrails: ['DLP_PII_AUTO_REDACT', 'OWASP_LLM06_EXCESSIVE_AGENCY_LOCK'],
    lastAccessed: 'Just now'
  },
  {
    id: 'app-zendesk',
    name: 'Zendesk Customer Service',
    category: 'crm',
    categoryLabel: 'CRM & Customer Ops',
    icon: '🎧',
    description: 'Help desk ticketing, customer inquiries, and SLA escalations.',
    endpoint: 'https://acme-support.zendesk.com/api/v2',
    permissionTier: 'ALLOW',
    connectedAgents: [
      { id: 'agent-support-01', name: 'Customer Support Triage Agent' }
    ],
    allowedActions: ['read_ticket_details', 'post_public_reply', 'set_ticket_priority', 'search_kb_articles'],
    reviewActions: ['escalate_to_legal', 'issue_courtesy_credit'],
    blockedActions: ['export_bulk_customer_data', 'read_unredacted_pii', 'delete_ticket_history'],
    rateLimit: '250 calls/min',
    enforcedGuardrails: ['DLP_SSN_CREDITCARD_REDACT', 'PROMPT_INJECTION_SCANNER'],
    lastAccessed: '6 minutes ago'
  },
  {
    id: 'app-aws',
    name: 'Amazon Web Services (AWS API)',
    category: 'devops',
    categoryLabel: 'Cloud Infrastructure & DevOps',
    icon: '⚡',
    description: 'Compute instances, S3 storage buckets, and serverless infrastructure.',
    endpoint: 'https://*.amazonaws.com',
    permissionTier: 'REVIEW',
    connectedAgents: [
      { id: 'agent-devops-01', name: 'DevOps & SRE Autopilot Agent' }
    ],
    allowedActions: ['get_cloudwatch_metrics', 'describe_ec2_instances', 'read_s3_readiness_probe', 'list_lambda_functions'],
    reviewActions: ['restart_ec2_instance', 'scale_autoscaling_group', 'purge_sqs_queue'],
    blockedActions: ['delete_s3_bucket', 'drop_rds_database', 'modify_iam_root_policies', 'terminate_production_cluster'],
    rateLimit: '60 calls/min',
    enforcedGuardrails: ['IMMUTABLE_PROD_DELETE_LOCK', 'IAM_PRIVILEGE_ESCALATION_SHIELD'],
    lastAccessed: '18 minutes ago'
  },
  {
    id: 'app-github',
    name: 'GitHub Enterprise Repositories',
    category: 'devops',
    categoryLabel: 'Cloud Infrastructure & DevOps',
    icon: '🐙',
    description: 'Code repositories, pull requests, issue tracking, and CI/CD pipelines.',
    endpoint: 'https://api.github.com/repos/acme-org',
    permissionTier: 'ALLOW',
    connectedAgents: [
      { id: 'agent-devops-01', name: 'DevOps & SRE Autopilot Agent' },
      { id: 'agent-qa-02', name: 'Automated Test Runner' }
    ],
    allowedActions: ['read_pull_request', 'list_failed_ci_jobs', 'comment_code_review', 'create_bug_issue'],
    reviewActions: ['merge_pr_to_main', 'trigger_production_deploy_workflow'],
    blockedActions: ['force_push_to_main', 'delete_repository', 'leak_github_actions_secrets'],
    rateLimit: '180 calls/min',
    enforcedGuardrails: ['BRANCH_PROTECTION_ENFORCEMENT', 'SECRET_LEAK_HEURISTIC'],
    lastAccessed: '35 minutes ago'
  },
  {
    id: 'app-postgres',
    name: 'PostgreSQL / Snowflake Warehouse',
    category: 'database',
    categoryLabel: 'Databases & Storage',
    icon: '🐘',
    description: 'Primary relational database and analytical data warehouse.',
    endpoint: 'postgres://db.internal.acme.net:5432/production',
    permissionTier: 'ALLOW',
    connectedAgents: [
      { id: 'agent-invoice-01', name: 'Financial Invoice & Disbursement Agent' },
      { id: 'agent-support-01', name: 'Customer Support Triage Agent' }
    ],
    allowedActions: ['select_redacted_rows', 'insert_audit_record', 'update_order_status'],
    reviewActions: ['alter_customer_billing_table', 'bulk_update_records_over_1000'],
    blockedActions: ['drop_table', 'truncate_table', 'drop_database', 'execute_raw_unparameterized_sql'],
    rateLimit: '500 queries/min',
    enforcedGuardrails: ['SQL_INJECTION_AST_SHIELD', 'DESTRUCTIVE_DDL_HARD_BLOCK'],
    lastAccessed: '1 minute ago'
  },
  {
    id: 'app-slack',
    name: 'Slack Enterprise Grid',
    category: 'messaging',
    categoryLabel: 'Messaging & Notifications',
    icon: '💬',
    description: 'Internal team workspaces, operational channels, and security alerts.',
    endpoint: 'https://slack.com/api',
    permissionTier: 'ALLOW',
    connectedAgents: [
      { id: 'agent-invoice-01', name: 'Financial Invoice & Disbursement Agent' },
      { id: 'agent-devops-01', name: 'DevOps & SRE Autopilot Agent' },
      { id: 'agent-support-01', name: 'Customer Support Triage Agent' }
    ],
    allowedActions: ['post_notification_to_channel', 'send_maker_checker_alert', 'reply_in_thread'],
    reviewActions: ['broadcast_to_general_channel'],
    blockedActions: ['read_private_dm_history', 'snoop_executive_channels', 'export_workspace_messages'],
    rateLimit: '100 msgs/min',
    enforcedGuardrails: ['PRIVATE_CHANNEL_ISOLATION', 'INTERNAL_COMM_AUDIT_LOG'],
    lastAccessed: '4 minutes ago'
  }
];

export const AppPermissionMap: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [expandedAppId, setExpandedAppId] = useState<string | null>(null);

  const categories = [
    { id: 'all', label: 'All Applications' },
    { id: 'fintech', label: 'Financial & ERP' },
    { id: 'crm', label: 'CRM & Customer Ops' },
    { id: 'devops', label: 'Cloud & DevOps' },
    { id: 'database', label: 'Databases & Storage' },
    { id: 'messaging', label: 'Messaging & Alerts' }
  ];

  const filteredApps = APPS_DATA.filter((app) => {
    const matchesCat = selectedCategory === 'all' || app.category === selectedCategory;
    const matchesTier = selectedTier === 'all' || app.permissionTier === selectedTier;
    const q = searchQuery.toLowerCase().trim();
    const matchesQuery = !q || 
      app.name.toLowerCase().includes(q) || 
      app.description.toLowerCase().includes(q) ||
      app.endpoint.toLowerCase().includes(q) ||
      app.connectedAgents.some(a => a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q)) ||
      app.allowedActions.some(act => act.toLowerCase().includes(q)) ||
      app.blockedActions.some(act => act.toLowerCase().includes(q));

    return matchesCat && matchesTier && matchesQuery;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Overview Banner */}
      <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-4 border border-brand-200/80 dark:border-brand-500/20 shadow-sm bg-gradient-to-r from-brand-50/50 via-white to-indigo-50/30 dark:from-brand-950/20 dark:via-slate-900/60 dark:to-slate-900/40">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="p-3.5 rounded-2xl bg-brand-600 text-white shadow-lg shadow-brand-500/20">
              <Network className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  Application & Tool Permission Map
                </h2>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300 font-bold border border-brand-200 dark:border-brand-500/30">
                  Zero-Trust Topology
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
                High-level visual topology showing all downstream services, databases, and third-party APIs connected to your AI agents, along with enforced permission boundaries and financial caps.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <div className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center shadow-xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">Connected Apps</span>
              <span className="text-lg font-black text-slate-900 dark:text-white font-mono">{APPS_DATA.length}</span>
            </div>
            <div className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center shadow-xs">
              <span className="text-[10px] font-bold text-emerald-500 uppercase block">Whitelisted</span>
              <span className="text-lg font-black text-emerald-600 dark:text-emerald-400 font-mono">
                {APPS_DATA.reduce((acc, a) => acc + a.allowedActions.length, 0)}
              </span>
            </div>
            <div className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center shadow-xs">
              <span className="text-[10px] font-bold text-rose-500 uppercase block">Hard Blocked</span>
              <span className="text-lg font-black text-rose-600 dark:text-rose-400 font-mono">
                {APPS_DATA.reduce((acc, a) => acc + a.blockedActions.length, 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Visual Legend */}
        <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400">
          <span className="font-bold text-slate-800 dark:text-slate-200">Permission Hierarchy:</span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-xs" />
            <span>ALLOW: Pre-approved read/mutation</span>
          </span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-amber-600 dark:text-amber-400">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-xs" />
            <span>REVIEW: Dual-control Maker-Checker gate</span>
          </span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-rose-600 dark:text-rose-400">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-xs" />
            <span>BLOCK: Prohibited destructive action / leak</span>
          </span>
        </div>
      </div>

      {/* Filter Bar & Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Category Pills */}
        <div className="flex flex-wrap gap-1.5">
          {categories.map((cat) => {
            const isCur = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                  isCur
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Search & Tier Dropdown */}
        <div className="flex items-center gap-2">
          <div className="relative flex-grow sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Filter by app or tool action…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs rounded-xl pl-9 pr-3 py-2 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500 transition"
            />
          </div>

          <select
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs rounded-xl px-3 py-2 text-slate-800 dark:text-slate-200 font-semibold focus:outline-none focus:border-brand-500 transition cursor-pointer"
          >
            <option value="all">All Tiers</option>
            <option value="ALLOW">ALLOW Tier</option>
            <option value="REVIEW">REVIEW Tier</option>
            <option value="BLOCK">BLOCK Tier</option>
          </select>
        </div>
      </div>

      {/* Connected Applications Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filteredApps.map((app) => {
          const isExpanded = expandedAppId === app.id;
          return (
            <div
              key={app.id}
              className="glass-panel p-5 sm:p-6 rounded-3xl border border-slate-200 dark:border-slate-800/80 space-y-4 hover:shadow-md transition-all bg-white/80 dark:bg-slate-900/60 flex flex-col justify-between"
            >
              <div className="space-y-3">
                {/* Header Row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl p-2 rounded-2xl bg-slate-100 dark:bg-slate-800 shrink-0">
                      {app.icon}
                    </span>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                          {app.name}
                        </h3>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-semibold">
                          {app.categoryLabel}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                        {app.description}
                      </p>
                    </div>
                  </div>

                  <span className={`text-[10px] font-mono px-2.5 py-1 rounded-full font-bold uppercase shrink-0 shadow-xs ${
                    app.permissionTier === 'ALLOW'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30'
                      : app.permissionTier === 'REVIEW'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30'
                      : 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300 border border-rose-300 dark:border-rose-500/30'
                  }`}>
                    {app.permissionTier} Tier
                  </span>
                </div>

                {/* Resource Endpoint / Key Metric */}
                <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800 text-xs font-mono text-slate-700 dark:text-slate-300 flex items-center justify-between gap-2 overflow-hidden">
                  <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="font-bold text-brand-600 dark:text-brand-400">URI:</span> {app.endpoint}
                  </span>
                  <span className="text-[10px] text-slate-400 font-sans font-semibold shrink-0">
                    Rate: {app.rateLimit}
                  </span>
                </div>

                {/* Financial Velocity / Cap Badge if present */}
                {app.financialCap && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300 font-semibold bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 rounded-xl border border-amber-200 dark:border-amber-900/40">
                    <DollarSign className="w-3.5 h-3.5" />
                    <span>Velocity Limit: {app.financialCap}</span>
                  </div>
                )}

                {/* Connected Agent Inventory */}
                <div className="space-y-1 text-xs">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Connected AI Agents ({app.connectedAgents.length}):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {app.connectedAgents.map((ag) => (
                      <span
                        key={ag.id}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg bg-brand-50 dark:bg-brand-950/30 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-900/40 font-medium"
                      >
                        <Cpu className="w-3 h-3 text-brand-500" />
                        <span>{ag.name}</span>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Expandable Permissions Breakdown */}
                {isExpanded && (
                  <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800 animate-fade-in text-xs">
                    
                    {/* Whitelisted Allowed Actions */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>Pre-Approved Tool Actions (ALLOW):</span>
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {app.allowedActions.map((act, i) => (
                          <span key={i} className="text-[10.5px] font-mono px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
                            {act}()
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Dual-Control Review Actions */}
                    {app.reviewActions.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Maker-Checker Approval Required (REVIEW):</span>
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {app.reviewActions.map((act, i) => (
                            <span key={i} className="text-[10.5px] font-mono px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
                              {act}()
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hard Blocked Actions */}
                    {app.blockedActions.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-rose-600 dark:text-rose-400 flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          <span>Strictly Prohibited & Dropped (BLOCK):</span>
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {app.blockedActions.map((act, i) => (
                            <span key={i} className="text-[10.5px] font-mono px-2 py-0.5 rounded bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60">
                              {act}()
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Active Guardrails */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400 block">
                        Enforced AST Guardrails:
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {app.enforcedGuardrails.map((g, i) => (
                          <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                            🛡️ {g}
                          </span>
                        ))}
                      </div>
                    </div>

                  </div>
                )}
              </div>

              {/* Bottom Toggle Button */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-500">
                <span>Last active: <strong className="text-slate-700 dark:text-slate-300 font-medium">{app.lastAccessed}</strong></span>
                <button
                  type="button"
                  onClick={() => setExpandedAppId(isExpanded ? null : app.id)}
                  className="text-brand-600 dark:text-brand-400 font-bold hover:underline inline-flex items-center gap-1 cursor-pointer"
                >
                  <span>{isExpanded ? 'Collapse Details' : 'View Permissions & Rules'}</span>
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};
